import { CarePath } from '../types/content'

const IMG = {
  early: 'https://cdn.magicpatterns.com/patterns/generated-images/ab3b24cc-ed84-43f8-9b8e-798db03c9eac.jpg',
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/7ee1d032-b683-40a1-8715-5b04977dfa09.jpg',
  night: 'https://cdn.magicpatterns.com/patterns/generated-images/d36a1bde-1090-4f1f-ba2f-3b64fc75870e.jpg',
  strength:
    'https://cdn.magicpatterns.com/patterns/generated-images/2c905241-a3be-45b5-8bdf-82b23e06bc80.jpg',
  together:
    'https://cdn.magicpatterns.com/patterns/generated-images/2fe002d8-9e53-4603-a692-99bb4030a2b4.jpg',
  clinician:
    'https://cdn.magicpatterns.com/patterns/generated-images/35d0b2c1-ff93-4c83-b570-ddca6cd5181e.jpg',
}

export const carePaths: CarePath[] = [
  {
    slug: 'perimenopause-early-signals',
    name: 'Early Signals',
    category: 'Perimenopause',
    summary: 'For the woman who has been told she is too young.',
    description:
      'Cycles that shifted before anything else did. PMS that turned vicious. Anxiety at 4pm on a Tuesday for no reason at all. This path is built for the years when everything is changing and nothing shows up on a test — the years when women are most often sent home.',
    forYouIf: [
      'You are somewhere between 35 and 47',
      'Your cycle length has changed by a week or more',
      'Your PMS is markedly worse than it used to be',
      'Someone has told you that you are too young for this',
    ],
    priceFrom: 89,
    cadence: 'per month',
    image: IMG.early,
    eligibility: 'Cycle tracking reviewed before your first consult. Thyroid and ferritin screened.',
    includes: [
      'A 45-minute first consult — long enough to tell the whole story',
      'Cycle and symptom mapping across three months',
      'Thyroid, ferritin, vitamin D and HbA1c screening',
      'Cycle-timed progesterone where appropriate',
      'A written summary you can take to any other clinician',
      'Unlimited messaging with your care team',
    ],
    protocol: [
      {
        title: 'Tell it once, properly',
        detail:
          'A long first consult with a clinician who already believes you. No five-minute triage, no starting from scratch each time.',
      },
      {
        title: 'Rule out the mimics',
        detail:
          'Thyroid disease, iron deficiency and insulin resistance all look like this and are all treatable. We check before we attribute anything to hormones.',
      },
      {
        title: 'Map the pattern',
        detail:
          'Three months of cycle and symptom tracking turns a vague story into a clinical picture — and makes treatment precise rather than approximate.',
      },
      {
        title: 'Treat what is disrupting you most',
        detail:
          'Usually sleep and mood first. Cycle-timed progesterone, then reassessment at six and twelve weeks.',
      },
    ],
    options: [
      {
        name: 'Micronised progesterone',
        form: 'Oral capsule, nightly or cycle-timed',
        note: 'Body-identical. Doubles as a sleep and anxiety intervention.',
      },
      {
        name: 'Hormonal IUD',
        form: 'Referral for placement',
        note: 'For heavy bleeding alongside contraception. Coordinated, not prescribed by us.',
      },
      {
        name: 'Iron repletion',
        form: 'Oral, dose per ferritin',
        note: 'Heavy perimenopausal bleeding causes iron deficiency far more often than it is caught.',
      },
    ],
  },
  {
    slug: 'hormone-therapy',
    name: 'Hormone Therapy',
    category: 'Menopause',
    summary: 'Estradiol and progesterone, dosed to how you feel — not to a lab number.',
    description:
      'Hot flushes, night sweats, the sleep loss underneath them. This is the path for women who want hormone therapy assessed properly: what the evidence says, whether you are a candidate, what the risk actually is in numbers, and what to do instead if the answer is no.',
    forYouIf: [
      'Hot flushes or night sweats are disrupting your days',
      'You want a straight answer about whether HRT is safe for you',
      'You have been refused before without an explanation',
      'You have been on hormone therapy and it was never titrated',
    ],
    priceFrom: 119,
    cadence: 'per month',
    image: IMG.hero,
    eligibility:
      'Full contraindication review including clot, stroke and cancer history. Blood pressure required.',
    includes: [
      'Candidacy assessment with the reasoning explained, not just the verdict',
      'Transdermal estradiol with micronised progesterone where indicated',
      'Non-hormonal alternatives given equal consideration',
      'Titration every six weeks until symptoms — not numbers — settle',
      'Your personal risk discussed in absolute numbers',
      'Medication shipped, or sent to your pharmacy — your choice',
    ],
    protocol: [
      {
        title: 'Candidacy, explained',
        detail:
          'Clot history, migraine with aura, cancer history, liver disease, blood pressure. If hormone therapy is not right for you, you will be told exactly why.',
      },
      {
        title: 'Start low, transdermal',
        detail:
          'Patch, gel or spray rather than a tablet — it avoids first-pass liver metabolism and the clot risk that comes with it. Progesterone nightly if you have a uterus.',
      },
      {
        title: 'Titrate to symptoms',
        detail:
          'Reviewed at six and twelve weeks. Dose follows how you feel. We do not chase a target estradiol level, because there isn’t one.',
      },
      {
        title: 'Review annually, deliberately',
        detail:
          'Not an automatic stop date. A real conversation each year about whether continuing still makes sense for you.',
      },
    ],
    options: [
      {
        name: 'Estradiol transdermal patch',
        form: 'Twice weekly, 0.025–0.1mg/day',
        note: 'FDA-approved. First-line where there is any clot or migraine consideration.',
      },
      {
        name: 'Estradiol gel',
        form: 'Daily pump, dose titratable',
        note: 'For skin that reacts to adhesive, or where finer dose steps are useful.',
      },
      {
        name: 'Micronised progesterone',
        form: 'Oral 100–200mg nightly',
        note: 'Required for endometrial protection if you have a uterus. Sedating, in a useful way.',
      },
      {
        name: 'Fezolinetant',
        form: 'Oral, daily',
        note: 'Non-hormonal, FDA-approved for vasomotor symptoms. Liver monitoring required.',
      },
    ],
  },
  {
    slug: 'sleep-mood-fog',
    name: 'Sleep, Mood & Fog',
    category: 'Perimenopause',
    summary: 'The cluster most often misdiagnosed as depression.',
    description:
      'Waking at 3am. Losing words mid-sentence. Rage you cannot explain and are ashamed of. This trio arrives together because it shares a mechanism — and it is the cluster most likely to be met with an antidepressant and nothing else.',
    forYouIf: [
      'You wake between 2am and 4am most nights',
      'Brain fog has made you worry about your memory',
      'Your irritability feels chemical rather than circumstantial',
      'You were offered an SSRI without anyone asking about your cycle',
    ],
    priceFrom: 109,
    cadence: 'per month',
    image: IMG.night,
    eligibility: 'Mood screening at intake. Sleep apnoea risk assessed — it is missed in women constantly.',
    includes: [
      'Sleep architecture and 3am-waking assessment',
      'Formal mood screening, with hormones considered alongside',
      'Sleep apnoea risk screening',
      'Micronised progesterone at night where appropriate',
      'Referral to CBT-I where insomnia has become self-sustaining',
      'Cognitive symptom tracking so you can see the fog lifting',
    ],
    protocol: [
      {
        title: 'Find out what is waking you',
        detail:
          'A flush, a cortisol rise, anxiety, or apnoea. They need different treatment and they get confused with each other constantly.',
      },
      {
        title: 'Restore the calming buffer',
        detail:
          'Micronised progesterone at night replaces what has fallen away. For many women this alone changes the 3am pattern within two weeks.',
      },
      {
        title: 'Stabilise the swings',
        detail:
          'Transdermal estradiol smooths the volatility driving mood and flushes, where you are a candidate.',
      },
      {
        title: 'Treat mood on its own terms too',
        detail:
          'If depression is present, it gets treated properly. Hormones considered first does not mean hormones only.',
      },
    ],
    options: [
      {
        name: 'Micronised progesterone',
        form: 'Oral 100–200mg at night',
        note: 'The most useful single intervention for 3am waking in this population.',
      },
      {
        name: 'Estradiol transdermal',
        form: 'Patch or gel',
        note: 'Reduces night sweats, which reduces the micro-arousals fragmenting your sleep.',
      },
      {
        name: 'SSRI / SNRI',
        form: 'Oral, daily',
        note: 'Where mood warrants it, or where hormones are contraindicated. Also reduces flushes.',
      },
      {
        name: 'CBT-I referral',
        form: 'Six-session programme',
        note: 'The strongest evidence base of anything in insomnia. Not a substitute for treating the cause.',
      },
    ],
  },
  {
    slug: 'midlife-metabolism',
    name: 'Midlife Metabolism',
    category: 'Whole body',
    summary: 'Your body changed shape. Nothing about you failed.',
    description:
      'Falling estrogen moves fat storage to the abdomen and reduces insulin sensitivity, while muscle quietly declines. This path is about strength, protein and metabolic health — deliberately not about a goal weight. There are no scales here and no before-and-after photographs.',
    forYouIf: [
      'Your body changed shape while your habits did not',
      'Strength and energy matter more to you than a number',
      'You want metabolic markers looked at properly',
      'You have been handed a calorie target and nothing else',
    ],
    priceFrom: 99,
    cadence: 'per month',
    image: IMG.strength,
    eligibility: 'HbA1c, lipids and blood pressure reviewed. Eating disorder history screened with care.',
    includes: [
      'Metabolic panel: HbA1c, fasting insulin, lipids, liver function',
      'A progressive resistance training plan, built around what you can access',
      'Protein targets set for muscle preservation, not restriction',
      'GLP-1 medication where clinically indicated — never as a first move',
      'Muscle-preserving protocol alongside any weight medication',
      'Quarterly markers, so progress is measured in more than kilograms',
    ],
    protocol: [
      {
        title: 'Measure what matters',
        detail:
          'Insulin sensitivity, lipids, blood pressure and strength. Weight is one line in a longer report, not the report.',
      },
      {
        title: 'Load the muscle',
        detail:
          'Progressive resistance work two to three times weekly. The single highest-yield intervention in midlife, and the one women are least often prescribed.',
      },
      {
        title: 'Feed it',
        detail:
          'Protein at a level most women find surprisingly high, distributed across the day.',
      },
      {
        title: 'Medication, if it earns its place',
        detail:
          'Where a GLP-1 is clinically indicated we prescribe it — with resistance training and protein already in place, because muscle loss is the real risk here.',
      },
    ],
    options: [
      {
        name: 'Tirzepatide',
        form: 'Weekly subcutaneous injection',
        note: 'FDA-approved. Prescribed where clinically indicated, alongside a muscle-preservation plan.',
      },
      {
        name: 'Metformin',
        form: 'Oral, daily',
        note: 'For insulin resistance where indicated. Inexpensive and long-established.',
      },
      {
        name: 'Vitamin D3 + K2',
        form: 'Oral, daily',
        note: 'Repleted to level, not prescribed by default. Deficiency is common and easily missed.',
      },
    ],
  },
  {
    slug: 'intimacy-vaginal-health',
    name: 'Intimacy & Vaginal Health',
    category: 'Whole body',
    summary: 'Named plainly, because nobody else will.',
    description:
      'Dryness, burning, pain during sex, urgency, leaking, recurrent urinary tract infections, and desire that quietly disappeared. Around half of post-menopausal women live with this. Fewer than one in ten are treated. It is among the most treatable things on this entire site.',
    forYouIf: [
      'Sex has become uncomfortable or painful',
      'You have had more urinary tract infections than you used to',
      'Dryness has not improved with over-the-counter products',
      'Nobody has ever asked you about any of this',
    ],
    priceFrom: 79,
    cadence: 'per month',
    image: IMG.together,
    eligibility:
      'Local estrogen is appropriate for the large majority of women, including many breast cancer survivors after discussion.',
    includes: [
      'A consult where you are actually asked the questions',
      'Local vaginal estrogen — cream, tablet or ring',
      'Moisturiser and lubricant guidance that distinguishes between them',
      'Pelvic floor physiotherapy referral',
      'Recurrent UTI prevention strategy',
      'Low-dose testosterone assessment where distressing low desire persists',
    ],
    protocol: [
      {
        title: 'Ask the questions properly',
        detail:
          'Where the pain is, when it started, whether it is entry or deep, what you have already tried. Specific questions, because vague ones get vague answers.',
      },
      {
        title: 'Restore the tissue',
        detail:
          'Local vaginal estrogen, daily for two weeks then twice weekly. Expect eight to twelve weeks for full effect, and improvement well before that.',
      },
      {
        title: 'Release the guarding',
        detail:
          'Months of anticipating pain builds protective pelvic floor tension that outlasts the original cause. Physiotherapy addresses it.',
      },
      {
        title: 'Then, desire',
        detail:
          'Once pain and exhaustion are dealt with, desire often returns on its own. Where it does not, low-dose testosterone is assessed and monitored.',
      },
    ],
    options: [
      {
        name: 'Vaginal estradiol cream',
        form: 'Applied twice weekly after loading',
        note: 'FDA-approved. Minimal systemic absorption at standard dosing.',
      },
      {
        name: 'Estradiol vaginal insert',
        form: 'Twice weekly tablet',
        note: 'Less messy than cream. Same evidence base.',
      },
      {
        name: 'Testosterone cream, low dose',
        form: 'Daily topical, female dosing',
        note: 'Off-label in the US for women. Good evidence for distressing low desire post-menopause; monitored quarterly.',
      },
      {
        name: 'Vaginal moisturiser',
        form: 'Non-hormonal, every 2–3 days',
        note: 'Different from a lubricant. Most women are only ever told about lubricant.',
      },
    ],
  },
  {
    slug: 'bones-heart-long-view',
    name: 'Bones, Heart & the Long View',
    category: 'Long view',
    summary: 'The decade that decides the next three.',
    description:
      'Bone loss accelerates sharply in the years around menopause, and cardiovascular risk changes as estrogen falls. Neither announces itself. This path is quiet, unglamorous, and probably the most consequential thing on this site.',
    forYouIf: [
      'You are approaching or past your final period',
      'There is osteoporosis or early heart disease in your family',
      'You want to know your numbers rather than assume them',
      'You are planning for your seventies, not just this year',
    ],
    priceFrom: 89,
    cadence: 'per month',
    image: IMG.clinician,
    eligibility: 'DEXA referral coordinated where indicated by age or risk factors.',
    includes: [
      'Fracture risk assessment and DEXA referral',
      'Full lipid panel including ApoB and Lp(a)',
      'Blood pressure monitoring, properly, over time',
      'Calcium, vitamin D and protein adequacy review',
      'Weight-bearing and impact exercise programming',
      'Annual review of whether hormone therapy still fits the picture',
    ],
    protocol: [
      {
        title: 'Establish the baseline',
        detail:
          'Bone density where indicated, full lipids including ApoB, blood pressure over several readings rather than one.',
      },
      {
        title: 'Load the skeleton',
        detail:
          'Impact and resistance work. Bone responds to load and to very little else.',
      },
      {
        title: 'Address the modifiable',
        detail:
          'Blood pressure, lipids, and hormone therapy where you are a candidate and within the window where it helps.',
      },
      {
        title: 'Watch the trend',
        detail:
          'Annual markers. What matters here is direction over years, not any single result.',
      },
    ],
    options: [
      {
        name: 'Estradiol transdermal',
        form: 'Patch or gel',
        note: 'Reduces fracture risk. Strongest where started within ten years of menopause.',
      },
      {
        name: 'Vitamin D3 + K2',
        form: 'Oral, daily',
        note: 'Dosed to your measured level rather than a default.',
      },
      {
        name: 'Statin therapy',
        form: 'Oral, daily',
        note: 'Where lipid profile and overall risk justify it. Discussed with the numbers in front of you.',
      },
    ],
  },
]

export function getCarePath(slug: string): CarePath | undefined {
  return carePaths.find((path) => path.slug === slug)
}

export const carePathCategories = [
  'All',
  'Perimenopause',
  'Menopause',
  'Whole body',
  'Long view',
] as const
